/**
 * Retailer and Environment Access Types and Utilities
 * Shared access level hierarchy and permission checking utilities.
 * @packageDocumentation
 */

/**
 * Union type for retailer access levels (view-only, can edit, admin).
 */
export type RetailerAccessLevel = 'view' | 'edit' | 'admin';
/**
 * Union type for retailer account status states.
 */
export type RetailerStatus = 'active' | 'inactive' | 'pending' | 'suspended';
/**
 * Union type for supported deployment environments.
 */
export type Environment = 'production' | 'staging' | 'development';

/**
 * Summary information about a retailer account.
 */
export interface RetailerSummary {
  name: string;
  retailerCode: string;
  status: RetailerStatus;
  accessLevel: RetailerAccessLevel;
  /** Whether this retailer has access to development environment (if false, only production/staging) */
  isDevelopmentEnvironment?: boolean;
}

/**
 * Environment summary for API responses.
 * Used when listing environments a user has access to.
 */
export interface EnvironmentSummary {
  retailerCode: string;
  retailerName: string;
  status: RetailerStatus;
  environment: Environment;
  productionAccess: boolean;
  accessLevel: RetailerAccessLevel;
}

/**
 * Retailer with its accessible environments.
 */
export interface RetailerWithEnvironments {
  retailerCode: string;
  retailerName: string;
  status: RetailerStatus;
  environments: EnvironmentSummary[];
  /** Whether this retailer has access to development environment (if false, only production/staging) */
  isDevelopmentEnvironment?: boolean;
}

/**
 * Retailers list response from API.
 */
export interface RetailersListResponse {
  total: number;
  retailers: RetailerWithEnvironments[];
}

/**
 * Access level hierarchy for permission comparisons.
 * Higher values indicate more permissive access levels.
 */
export const ACCESS_HIERARCHY: Readonly<Record<RetailerAccessLevel, number>> = {
  view: 0,
  edit: 1,
  admin: 2,
} as const;

/**
 * Environment display names for UI.
 */
export const ENVIRONMENT_DISPLAY_NAMES: Readonly<Record<Environment, string>> = {
  staging: 'Staging',
  production: 'Production',
  development: 'Development',
} as const;

/**
 * Environment color coding for UI.
 */
export const ENVIRONMENT_COLORS: Readonly<Record<Environment, string>> = {
  production: 'red',
  staging: 'yellow',
  development: 'green',
} as const;

/**
 * Determines if a retailer access level meets minimum requirement.
 *
 * @param { RetailerAccessLevel } userLevel The user's current access level.
 * @param { RetailerAccessLevel } requiredLevel The minimum required access level.
 * @returns { boolean } Returns true if user meets or exceeds required level.
 */
export const hasSufficientRetailerAccess = (
  userLevel: RetailerAccessLevel,
  requiredLevel: RetailerAccessLevel,
): boolean => ACCESS_HIERARCHY[userLevel] >= ACCESS_HIERARCHY[requiredLevel];
